import { useNavigate } from "react-router-dom";
import { DeliverableFile } from "@/pages/deliverables/page";

interface JobCardRef {
  id: number;
  jobNo: string;
  title: string;
}

const fileTypeConfig: Record<string, { color: string; bg: string; icon: string }> = {
  PDF: { color: "#ef4444", bg: "#fef2f2", icon: "ri-file-pdf-line" },
  DWG: { color: "#f59e0b", bg: "#fffbeb", icon: "ri-file-code-line" },
  KMZ: { color: "#10b981", bg: "#ecfdf5", icon: "ri-map-2-line" },
  KML: { color: "#10b981", bg: "#ecfdf5", icon: "ri-map-2-line" },
  XLSX: { color: "#059669", bg: "#d1fae5", icon: "ri-file-excel-line" },
  DXF: { color: "#f59e0b", bg: "#fffbeb", icon: "ri-file-code-line" },
  SHP: { color: "#8b5cf6", bg: "#f5f3ff", icon: "ri-map-pin-line" },
  JPG: { color: "#64748b", bg: "#f8fafc", icon: "ri-image-line" },
  ZIP: { color: "#64748b", bg: "#f8fafc", icon: "ri-folder-zip-line" },
};

const statusConfig: Record<string, { color: string; bg: string }> = {
  Draft: { color: "#64748b", bg: "#f8fafc" },
  "Under Review": { color: "#f59e0b", bg: "#fffbeb" },
  Approved: { color: "#3b82f6", bg: "#eff6ff" },
  Delivered: { color: "#10b981", bg: "#ecfdf5" },
};

const activityLog = [
  { action: "Status changed to Delivered", by: "Priya Sharma", time: "2026-03-30T14:30:00" },
  { action: "Version v3.0 uploaded", by: "Priya Sharma", time: "2026-03-30T14:00:00" },
  { action: "Status changed to Approved", by: "Priya Sharma", time: "2026-03-28T11:00:00" },
  { action: "Version v2.0 uploaded", by: "Priya Sharma", time: "2026-03-25T10:00:00" },
  { action: "File created (v1.0)", by: "Ravi Teja", time: "2026-03-20T09:00:00" },
];

interface FileDetailPanelProps {
  file: DeliverableFile;
  allFiles: DeliverableFile[];
  onClose: () => void;
  onStatusChange: (id: number, status: DeliverableFile["status"]) => void;
  onVersionHistory: (jobNo: string, fileName: string) => void;
}

export default function FileDetailPanel({ file, allFiles, onClose, onStatusChange, onVersionHistory }: FileDetailPanelProps) {
  const navigate = useNavigate();
  const ftCfg = fileTypeConfig[file.fileType] || fileTypeConfig.PDF;
  const stCfg = statusConfig[file.status] || statusConfig.Draft;
  const linkedJob: JobCardRef | undefined = undefined; // resolved via allFiles context

  const relatedFiles = allFiles.filter(
    (f) => f.jobNo === file.jobNo && f.id !== file.id
  );

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white flex flex-col overflow-hidden animate-slide-in-right"
        style={{ animation: "slideInRight 0.2s ease-out" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: ftCfg.bg }}>
              <i className={`${ftCfg.icon} text-xl`} style={{ color: ftCfg.color }} />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900 leading-tight">{file.fileType} File</p>
              <p className="text-xs text-gray-500">{file.version} · {file.size}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* File name */}
          <div className="px-5 py-4 border-b border-gray-50">
            <p className="text-base font-bold text-gray-900 break-all">{file.fileName}</p>
            <p className="text-xs text-gray-500 mt-1">{file.description}</p>
          </div>

          {/* Status + Actions */}
          <div className="px-5 py-4 border-b border-gray-50">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</span>
              <select
                value={file.status}
                onChange={(e) => onStatusChange(file.id, e.target.value as DeliverableFile["status"])}
                className="text-xs px-3 py-1.5 rounded-lg border font-semibold cursor-pointer focus:outline-none"
                style={{ color: stCfg.color, background: stCfg.bg, borderColor: stCfg.color + "40" }}
              >
                {["Draft", "Under Review", "Approved", "Delivered"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button className="flex-1 flex items-center justify-center gap-1.5 bg-amber-600 text-white rounded-xl py-2 text-xs font-semibold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap">
                <i className="ri-download-line" />Download
              </button>
              <button
                onClick={() => onVersionHistory(file.jobNo, file.fileName)}
                className="flex-1 flex items-center justify-center gap-1.5 border border-gray-200 text-gray-600 rounded-xl py-2 text-xs font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap"
              >
                <i className="ri-history-line" />Versions
              </button>
              <button className="w-9 h-9 flex items-center justify-center border border-gray-200 text-gray-500 rounded-xl cursor-pointer hover:bg-gray-50 transition-all">
                <i className="ri-share-line text-sm" />
              </button>
            </div>
            {linkedJob && (
              <button
                onClick={() => { onClose(); navigate(`/job-cards/${linkedJob.id}`); }}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-gray-50 hover:bg-amber-50 border border-gray-200 hover:border-amber-300 rounded-xl transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-list-3-line text-amber-600 text-sm" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold text-gray-800 group-hover:text-amber-700">{linkedJob.jobNo}</p>
                    <p className="text-xs text-gray-500 truncate max-w-48">{linkedJob.title}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">Open Job Card</span>
                  <i className="ri-arrow-right-line text-amber-500 text-sm" />
                </div>
              </button>
            )}
          </div>

          {/* Metadata */}
          <div className="px-5 py-4 border-b border-gray-50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">File Details</p>
            <div className="space-y-2.5">
              {[
                { label: "Job Card", value: file.jobNo, icon: "ri-file-list-3-line" },
                { label: "Job Title", value: file.jobTitle, icon: "ri-briefcase-line" },
                { label: "Category", value: file.category, icon: "ri-folder-line" },
                { label: "File Type", value: file.fileType, icon: "ri-file-info-line" },
                { label: "Version", value: file.version, icon: "ri-git-branch-line" },
                { label: "File Size", value: file.size, icon: "ri-hard-drive-2-line" },
                { label: "Uploaded By", value: file.uploadedBy, icon: "ri-user-line" },
                { label: "Upload Date", value: new Date(file.uploadedAt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }), icon: "ri-calendar-line" },
                { label: "Downloads", value: `${file.downloadCount} times`, icon: "ri-download-line" },
              ].map((row) => (
                <div key={row.label} className="flex items-start gap-3">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className={`${row.icon} text-gray-400 text-sm`} />
                  </span>
                  <span className="text-xs text-gray-500 w-24 flex-shrink-0">{row.label}</span>
                  <span className="text-xs font-medium text-gray-800 flex-1">{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Activity Log */}
          <div className="px-5 py-4 border-b border-gray-50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Activity Log</p>
            <div className="space-y-3">
              {activityLog.map((log, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-700">{log.action}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{log.by} · {new Date(log.time).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Related Files */}
          {relatedFiles.length > 0 && (
            <div className="px-5 py-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Other Files in {file.jobNo}</p>
              <div className="space-y-2">
                {relatedFiles.slice(0, 4).map((rf) => {
                  const rftCfg = fileTypeConfig[rf.fileType] || fileTypeConfig.PDF;
                  return (
                    <div key={rf.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all cursor-pointer">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: rftCfg.bg }}>
                        <i className={`${rftCfg.icon} text-sm`} style={{ color: rftCfg.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800 truncate">{rf.fileName}</p>
                        <p className="text-xs text-gray-400">{rf.category} · {rf.version}</p>
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">{rf.size}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
